import { Code, ConnectError } from "@connectrpc/connect";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { type Reservation, ReservationStatus } from "@viajero/proto/guest/v1";
import { ErrorStateView } from "@viajero/shared-ui/error-boundary";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryStateFallback } from "@/components/QueryStateFallback";
import { useGetReservation } from "@/features/reservation/api/useGetReservation";
import { ReservationSummaryCard } from "@/features/reservation/components/ReservationSummaryCard";
import { colors } from "@/theme/colors";

// 決済完了 Webhook の到達を待つ猶予 (§3.1)。これを超えて未確定なら案内を追加表示する。
const SLOW_CONFIRM_MS = 60_000;

/**
 * 予約完了画面。決済成功後の success_url (§7.1) で遷移し、GetReservation をポーリングして
 * 決済完了 (confirmed) を待つ (§3.1)。
 * - 決済待ち (pending_payment) の間は「決済確認中」を表示する。
 * - 一定時間 (SLOW_CONFIRM_MS) 経っても確定しない場合は、時間がかかっている旨とメール案内を出す。
 * - confirmed になったら予約内容 (店舗名・日時・予約番号カード + 商品画像 + ご購入内容 +
 *   キャンセルポリシー) を ReservationSummaryCard でキャンセル画面と共通表示する (JPTP_CANSEI-1420)。
 * - confirmed 以外の終端状態 (finalize_failed / canceled 等) はエラー案内を表示する。
 */
type Props = {
  // 購入時に退避した店舗詳細へ戻るコールバック。退避が無い (メール流入・別タブ) 場合は
  // undefined で、その場合は該当ボタンを出さずメール導線の案内だけを表示する。
  onBackToShop?: () => void;
};

export function ReservationCompleteStep({ onBackToShop }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useGetReservation();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setSlow(true), SLOW_CONFIRM_MS);
    return () => window.clearTimeout(id);
  }, []);

  const reservation = data?.reservation;
  const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
  // 退避が無い場合は「店舗詳細に戻る」を出さない (メール導線の案内へフォールバックする)。
  const backToShopAction = onBackToShop
    ? [{ label: t("errorPage.backToShop"), onClick: onBackToShop }]
    : [];

  // 予約トークンが無効・期限切れ等で取得できない場合はエラー表示 (ポーリングでデータ未取得のまま失敗)。
  if (!reservation) {
    return (
      <QueryStateFallback
        isLoading={isLoading}
        // 取得は成功したが reservation が空のケースも「見つからない」として扱う。
        // ここを素通りさせると status===undefined で決済確認中スピナーが回り続ける
        // (キャンセル系 2 画面と同じ扱いに揃える)。
        isNotFound={isNotFound || (!isLoading && !isError)}
        isError={isError}
        notFound={{
          title: t("reservationComplete.notFound"),
          description: t("reservationComplete.notFoundDescription"),
          actions: backToShopAction,
        }}
        error={{
          title: t("reservationComplete.error"),
          description: t("reservationComplete.errorDescription"),
          // 決済済みの可能性があるため再読み込みを主にする (再予約させると二重決済になる)。
          actions: [
            { label: t("errorPage.reload"), onClick: () => void refetch() },
            ...backToShopAction.map((a) => ({ ...a, variant: "outlined" as const })),
          ],
        }}
      />
    );
  }

  if (reservation?.status === ReservationStatus.CONFIRMED) {
    return <ConfirmedView reservation={reservation} />;
  }

  // 待機表示を続けるのは決済待ち (未着含む) の間だけ (§3.1)。CONFIRMED 以外の終端状態
  // (finalize_failed / canceled / refunded / refund_failed 等) はポーリングも停止しており、
  // 確定に至らないため、スピナーのまま固まらないようエラー案内を表示する。
  const status = reservation?.status;
  const isWaiting =
    status === undefined ||
    status === ReservationStatus.UNSPECIFIED ||
    status === ReservationStatus.PENDING_PAYMENT;

  if (!isWaiting) {
    return (
      <ErrorStateView
        severity="error"
        title={t("reservationComplete.failed")}
        description={t("reservationComplete.failedDescription")}
        // 全額返金されるため、店舗詳細に戻る導線を主にする (§7.7 の補償返金)。
        actions={backToShopAction}
      />
    );
  }

  // 決済待ち。確定を待っている状態を表示する。
  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 640,
        width: "100%",
        mx: "auto",
        textAlign: "center",
      }}
    >
      <CircularProgress sx={{ mt: 4, mb: 3 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text }}>
        {t("reservationComplete.confirming")}
      </Typography>
      {slow && (
        <Alert severity="info" sx={{ mt: 3, textAlign: "left" }}>
          {t("reservationComplete.confirmingSlow")}
        </Alert>
      )}
    </Box>
  );
}

function ConfirmedView({ reservation }: { reservation: Reservation }) {
  const { t } = useTranslation();

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 640, width: "100%", mx: "auto" }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: colors.text, textAlign: "center", pt: 2, pb: 5 }}
      >
        {t("reservationComplete.title")}
      </Typography>

      <ReservationSummaryCard
        reservation={reservation}
        message={t("reservationComplete.message")}
        showConfirmIcon
      />
    </Box>
  );
}
